// /src/ReminderModal.js
import React, { useState, useEffect } from "react";
import api from "./services/api";
import generatePDF from "./PDFGenerator";

export const getBilingualReminderTemplate = (q, lang = "both") => {
  if (!q) return "";
  const name = q.customer_name || "Customer";
  const qNo = q.quotation_no || "Quotation";
  const amt = q.net_total ? `₹${Number(q.net_total).toLocaleString("en-IN")}/-` : "";
  const formattedDate = q.date ? new Date(q.date).toLocaleDateString("en-GB") : "";
  const dt = formattedDate ? `(${formattedDate})` : "";

  if (lang === "hi") {
    return (
`*AVANTICA PUMPS PRIVATE LIMITED*
─────────────────────────
नमस्ते *${name}* जी, 🙏

आपको भेजे गए कोटेशन *#${qNo}* ${dt} ${amt ? `(रकम: *${amt}*)` : ""} के संबंध में यह एक विनम्र फॉलो-अप है।

क्या आप ऑर्डर आगे बढ़ाना चाहते हैं अथवा किसी तकनीकी जानकारी या बदलाव की आवश्यकता है?

कृपया हमें इस संदेश पर जवाब देकर बताएं। हम आपकी सेवा में सदैव उपस्थित हैं!

📞 +91 7984042112 / +91 9998546224
🌐 www.avanticapumps.com
_Avantica Pumps Pvt. Ltd._`
    );
  }

  if (lang === "hi_en") {
    return (
`*AVANTICA PUMPS PRIVATE LIMITED*
─────────────────────────
नमस्ते *${name}* जी, 🙏 | Namaste,

आपको भेजे गए कोटेशन *#${qNo}* ${dt} ${amt ? `(रकम: *${amt}*)` : ""} के संबंध में यह एक विनम्र फॉलो-अप है।
_This is a gentle follow-up regarding your quotation *#${qNo}* ${amt ? `for *${amt}*` : ""}_

क्या आप ऑर्डर आगे बढ़ाना चाहते हैं या किसी तकनीकी जानकारी/बदलाव की आवश्यकता है?
_Are you planning to proceed with the order or need any adjustments?_

कृपया हमें इस संदेश पर रिप्लाई देकर बताएं।
_Please reply to this message, our team is happy to assist you!_

📞 +91 7984042112 / +91 9998546224
🌐 www.avanticapumps.com
_Avantica Pumps Pvt. Ltd._`
    );
  }

  if (lang === "gu") {
    return (
`*AVANTICA PUMPS PRIVATE LIMITED*
─────────────────────────
નમસ્તે *${name}* જી, 🙏

આપને મોકલેલ ક્વોટેશન *#${qNo}* ${dt} ${amt ? `(રકમ: *${amt}*)` : ""} બાબતે આપનો અભિપ્રાય જાણવા આ નમ્ર ફોલો-અપ છે.

શું આપ ઓર્ડર કન્ફર્મ કરવા માંગો છો અથવા કોઈ ટેકનિકલ માહિતી કે ફેરફારની જરૂર છે?

કૃપા કરીને અમને આ મેસેજ પર રિપ્લાય આપી જણાવશો. અમે આપની સેવામાં હાજર છીએ!

📞 +91 7984042112 / +91 9998546224
🌐 www.avanticapumps.com
_Avantica Pumps Pvt. Ltd._`
    );
  }

  if (lang === "en") {
    return (
`*AVANTICA PUMPS PRIVATE LIMITED*
─────────────────────────
Namaste *${name}*, 🙏

This is a gentle follow-up regarding your quotation *#${qNo}* ${dt} ${amt ? `for *${amt}*` : ""} shared with you recently.

Are you planning to proceed with the order, or do you require any technical assistance or adjustments in the quotation?

Please reply to this message. Our team is always ready to assist you!

📞 +91 7984042112 / +91 9998546224
🌐 www.avanticapumps.com
_Avantica Pumps Pvt. Ltd._`
    );
  }

  // Default: Bilingual (Gujarati + English)
  return (
`*AVANTICA PUMPS PRIVATE LIMITED*
─────────────────────────
નમસ્તે *${name}* જી, 🙏 | Namaste,

આપને મોકલેલ ક્વોટેશન *#${qNo}* ${dt} ${amt ? `(રકમ: *${amt}*)` : ""} બાબતે આપનો અભિપ્રાય જાણવા આ નમ્ર ફોલો-અપ છે.
_This is a gentle follow-up regarding your quotation *#${qNo}* ${amt ? `for *${amt}*` : ""}_

શું આપ ઓર્ડર કન્ફર્મ કરવા માંગો છો અથવા કોઈ ટેકનિકલ માહિતી કે ફેરફારની જરૂર છે?
_Are you planning to proceed with the order or need any adjustments?_

કૃપા કરીને અમને આ મેસેજ પર રિપ્લાય આપી જણાવશો.
_Please reply to this message, our team is happy to assist you!_

📞 +91 7984042112 / +91 9998546224
🌐 www.avanticapumps.com
_Avantica Pumps Pvt. Ltd._`
  );
};

const ReminderModal = ({ quotation, onClose, onSuccess }) => {
  const [lang, setLang] = useState("both"); // "both" | "gu" | "en"
  const [message, setMessage] = useState("");
  const [attachPdf, setAttachPdf] = useState(true);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (quotation) {
      setMessage(getBilingualReminderTemplate(quotation, "both"));
    }
  }, [quotation]);

  const handleLangChange = (selectedLang) => {
    setLang(selectedLang);
    setMessage(getBilingualReminderTemplate(quotation, selectedLang));
  };

  const handleSendReminder = async () => {
    if (!quotation || !quotation.mobile) {
      setIsError(true);
      setFeedback("Customer mobile number is missing.");
      return;
    }

    try {
      setLoading(true);
      setFeedback("");
      setIsError(false);

      let pdfBase64 = null;
      if (attachPdf) {
        try {
          // Normalize items if string
          let itemsList = quotation.items;
          if (typeof itemsList === "string") {
            try {
              itemsList = JSON.parse(itemsList);
            } catch (e) {
              itemsList = [];
            }
          }

          pdfBase64 = generatePDF({
            customer: {
              name: quotation.customer_name,
              city: quotation.city,
              date: quotation.date ? new Date(quotation.date).toLocaleDateString("en-GB") : "",
              mobile: quotation.mobile,
              quotationNo: quotation.quotation_no,
            },
            categoryNames: [],
            selectedItems: itemsList || [],
            total: Number(quotation.total || 0),
            discountAmount: Number(quotation.discount_amount || 0),
            discountPercent: quotation.discount_percent,
            grandTotal: Number(quotation.net_total || 0),
            includeGst: quotation.include_gst !== 0 && quotation.include_gst !== false,
          }, "base64");
        } catch (pdfErr) {
          console.warn("Could not generate PDF base64 for reminder:", pdfErr);
        }
      }

      const res = await api.post("/whatsapp/send-reminder", {
        quotationId: quotation.id,
        mobile: quotation.mobile,
        quotationNo: quotation.quotation_no,
        customerName: quotation.customer_name,
        grandTotal: quotation.net_total,
        date: quotation.date ? new Date(quotation.date).toLocaleDateString("en-GB") : "",
        customMessage: message,
        pdfBase64,
        language: lang,
      });

      setIsError(false);
      setFeedback(res.data?.message || "Reminder sent on WhatsApp!");

      const finalCount = res.data?.reminder_count !== undefined
        ? Number(res.data.reminder_count)
        : (Number(quotation.reminder_count) || 0) + 1;

      if (onSuccess) {
        onSuccess({
          ...quotation,
          reminder_count: finalCount,
          last_reminder_date: new Date().toISOString(),
        });
      }

      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error("Error sending reminder:", err);
      setIsError(true);
      const errMsg =
        err.response?.data?.error ||
        (typeof err.response?.data === "string" ? err.response.data : null) ||
        err.message ||
        "Failed to send reminder. Ensure WhatsApp is connected in Navbar.";
      setFeedback(errMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!quotation) return null;

  const reminderCount = quotation.reminder_count || 0;
  const lastDateFormatted = quotation.last_reminder_date
    ? new Date(quotation.last_reminder_date).toLocaleDateString("en-GB")
    : null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-[fadeIn_0.2s_ease-out]">
      <div className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-3xl bg-slate-900 border border-purple-500/30 shadow-2xl p-6 md:p-8 flex flex-col gap-5 text-white custom-scrollbar">
        {/* Close Button */}
        <button
          onClick={onClose}
          disabled={loading}
          className="absolute top-5 right-5 w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/50 hover:text-white transition-all"
        >
          <i className="fas fa-times"></i>
        </button>

        {/* Modal Header */}
        <div className="flex items-start gap-3 border-b border-white/10 pb-4">
          <div className="w-12 h-12 rounded-2xl bg-[#25D366]/20 border border-[#25D366]/30 flex items-center justify-center text-[#25D366] text-xl flex-shrink-0">
            <i className="fab fa-whatsapp"></i>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl md:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-emerald-200 to-green-400">
                Send WhatsApp Reminder
              </h2>
              <span className="bg-purple-500/20 text-purple-300 text-xs px-2.5 py-0.5 rounded-full border border-purple-500/30 font-mono">
                #{quotation.quotation_no}
              </span>
            </div>
            <p className="text-white/60 text-sm mt-0.5">
              Follow up with <strong className="text-white">{quotation.customer_name}</strong> ({quotation.mobile})
            </p>
          </div>
        </div>

        {/* Info & Stats Strip */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 bg-white/5 p-3.5 rounded-2xl border border-white/5 text-xs">
          <div>
            <span className="text-white/40 block">Net Amount</span>
            <span className="text-emerald-400 font-bold text-sm font-mono">
              ₹{Number(quotation.net_total || 0).toLocaleString("en-IN")}
            </span>
          </div>
          <div>
            <span className="text-white/40 block">Quote Date</span>
            <span className="text-white/80 font-mono">
              {quotation.date ? new Date(quotation.date).toLocaleDateString("en-GB") : "-"}
            </span>
          </div>
          <div className="col-span-2 sm:col-span-1">
            <span className="text-white/40 block">Reminder History</span>
            <span className="text-amber-400 font-medium">
              {reminderCount > 0 ? (
                <>Sent {reminderCount}x {lastDateFormatted && `(last: ${lastDateFormatted})`}</>
              ) : (
                <span className="text-white/50">Never reminded yet</span>
              )}
            </span>
          </div>
        </div>

        {/* Language Tabs */}
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-purple-300/70 block mb-2">
            Select Reminder Language
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            <button
              type="button"
              onClick={() => handleLangChange("both")}
              className={`py-2 px-2.5 rounded-xl font-medium text-xs transition-all border flex items-center justify-center gap-1 ${
                lang === "both"
                  ? "bg-purple-600 border-purple-400 text-white shadow-lg shadow-purple-900/50"
                  : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
              }`}
              title="Bilingual Gujarati and English"
            >
              <span>🌐</span>
              <span className="font-bold">ગુજરાતી + Eng</span>
            </button>
            <button
              type="button"
              onClick={() => handleLangChange("hi_en")}
              className={`py-2 px-2.5 rounded-xl font-medium text-xs transition-all border flex items-center justify-center gap-1 ${
                lang === "hi_en"
                  ? "bg-purple-600 border-purple-400 text-white shadow-lg shadow-purple-900/50"
                  : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
              }`}
              title="Bilingual Hindi and English"
            >
              <span>🌐</span>
              <span className="font-bold">हिंदी + Eng</span>
            </button>
            <button
              type="button"
              onClick={() => handleLangChange("gu")}
              className={`py-2 px-2.5 rounded-xl font-medium text-xs transition-all border flex items-center justify-center gap-1 ${
                lang === "gu"
                  ? "bg-purple-600 border-purple-400 text-white shadow-lg shadow-purple-900/50"
                  : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
              }`}
            >
              <span>🇮🇳</span>
              <span className="font-bold">ગુજરાતી</span>
            </button>
            <button
              type="button"
              onClick={() => handleLangChange("hi")}
              className={`py-2 px-2.5 rounded-xl font-medium text-xs transition-all border flex items-center justify-center gap-1 ${
                lang === "hi"
                  ? "bg-purple-600 border-purple-400 text-white shadow-lg shadow-purple-900/50"
                  : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
              }`}
            >
              <span>🇮🇳</span>
              <span className="font-bold">हिंदी</span>
            </button>
            <button
              type="button"
              onClick={() => handleLangChange("en")}
              className={`py-2 px-2.5 rounded-xl font-medium text-xs transition-all border flex items-center justify-center gap-1 ${
                lang === "en"
                  ? "bg-purple-600 border-purple-400 text-white shadow-lg shadow-purple-900/50"
                  : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"
              }`}
            >
              <span>🇬🇧</span>
              <span className="font-bold">English</span>
            </button>
          </div>
        </div>

        {/* Message Editor */}
        <div>
          <div className="flex justify-between items-center mb-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-purple-300/70">
              Message Preview (Editable)
            </label>
            <span className="text-[11px] text-white/40">You can customize the text below</span>
          </div>
          <textarea
            rows={7}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="w-full bg-slate-950/80 border border-white/10 rounded-2xl p-4 text-sm text-white focus:outline-none focus:border-purple-500/50 transition-colors font-sans leading-relaxed resize-y"
            placeholder="Type your reminder message..."
          />
        </div>

        {/* PDF Attachment Option */}
        <div className="flex items-center gap-3 bg-white/5 p-3.5 rounded-2xl border border-white/5">
          <input
            id="attachPdfCheckbox"
            type="checkbox"
            checked={attachPdf}
            onChange={(e) => setAttachPdf(e.target.checked)}
            className="w-5 h-5 rounded border-white/20 text-purple-600 focus:ring-purple-500 bg-slate-900 cursor-pointer"
          />
          <label htmlFor="attachPdfCheckbox" className="text-sm cursor-pointer select-none text-white/90 flex-1">
            <span className="font-semibold block">📎 Re-attach Quotation PDF</span>
            <span className="text-xs text-white/50 block">Attaches the official Avantica branded quotation PDF along with this reminder.</span>
          </label>
        </div>

        {/* Feedback Display */}
        {feedback && (
          <div
            className={`p-4 rounded-2xl text-sm border flex items-center gap-3 ${
              isError
                ? "bg-red-500/10 border-red-500/30 text-red-300"
                : "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
            }`}
          >
            <i className={`fas ${isError ? "fa-exclamation-triangle" : "fa-check-circle"} text-lg`}></i>
            <span className="font-medium">{feedback}</span>
          </div>
        )}

        {/* Modal Actions */}
        <div className="flex items-center justify-end gap-3 pt-2 border-t border-white/10">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-5 py-3 rounded-xl font-bold text-sm bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 hover:text-white transition-all"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSendReminder}
            disabled={loading}
            className="px-6 py-3 rounded-xl font-bold text-sm bg-[#25D366] hover:bg-[#20bd5a] text-white shadow-lg shadow-green-900/30 transition-all flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span>Sending Reminder...</span>
              </>
            ) : (
              <>
                <i className="fab fa-whatsapp text-base"></i>
                <span>⚡ Send Reminder on WhatsApp</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReminderModal;
